import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Modal } from '@components/ui/Modal';

import type { InviteStaffRequest, StaffRole } from '../types';

const ROLES: StaffRole[] = ['ADMIN', 'PRACTITIONER', 'FRONTDESK'];

interface InviteStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteStaffRequest) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}

export function InviteStaffModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
}: InviteStaffModalProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().max(20, t('staff.inviteModal.titleMax')).optional(),
        firstName: z
          .string()
          .min(1, t('staff.inviteModal.firstNameRequired'))
          .max(100, t('staff.inviteModal.firstNameMax')),
        lastName: z
          .string()
          .min(1, t('staff.inviteModal.lastNameRequired'))
          .max(100, t('staff.inviteModal.lastNameMax')),
        email: z.string().email(t('staff.inviteModal.emailInvalid')),
        phone: z.string().optional(),
        roles: z
          .array(z.enum(['ADMIN', 'PRACTITIONER', 'FRONTDESK']))
          .min(1, t('staff.inviteModal.rolesRequired')),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', firstName: '', lastName: '', email: '', phone: '', roles: [] },
  });

  useEffect(() => {
    if (isOpen) reset({ title: '', firstName: '', lastName: '', email: '', phone: '', roles: [] });
  }, [isOpen, reset]);

  function onFormSubmit(values: FormValues) {
    onSubmit({
      title: values.title || undefined,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone || undefined,
      roles: values.roles,
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('staff.inviteModal.title')}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('staff.inviteModal.cancel')}
          </Button>
          <Button type="submit" form="invite-staff-form" isLoading={isSubmitting}>
            {t('staff.inviteModal.submit')}
          </Button>
        </>
      }
    >
      {errorMessage && (
        <div role="alert" className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {errorMessage}
        </div>
      )}
      <form
        id="invite-staff-form"
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
        className="space-y-4"
      >
        <div className="flex gap-3">
          <div className="w-24 shrink-0">
            <Input
              label={t('staff.inviteModal.titleLabel')}
              placeholder={t('staff.inviteModal.titlePlaceholder')}
              error={errors.title?.message}
              {...register('title')}
            />
          </div>
          <div className="flex flex-1 gap-3">
            <div className="flex-1">
              <Input
                label={t('staff.inviteModal.firstNameLabel')}
                error={errors.firstName?.message}
                autoFocus
                {...register('firstName')}
              />
            </div>
            <div className="flex-1">
              <Input
                label={t('staff.inviteModal.lastNameLabel')}
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>
          </div>
        </div>

        <Input
          label={t('staff.inviteModal.emailLabel')}
          type="email"
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label={t('staff.inviteModal.phoneLabel')}
          type="tel"
          {...register('phone')}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">
            {t('staff.inviteModal.rolesLabel')}
          </span>
          <div className="flex flex-wrap gap-4">
            {ROLES.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  value={role}
                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                  {...register('roles')}
                />
                {t(`staff.roles.${role}`)}
              </label>
            ))}
          </div>
          {errors.roles && (
            <p className="text-xs text-destructive">{errors.roles.message}</p>
          )}
        </div>
      </form>
    </Modal>
  );
}
